/**
 * Minimaler Markdown-Renderer (v1.3.0 Block 4, D-20).
 *
 * Bewusst klein und ohne Abhängigkeit: der Auftrag beschreibt "Frontend slim —
 * Editor + Live-Preview + Open/Save"; Toolbar, Slash-Commands, Bilder und
 * Embeds sind vertagt. Eine Markdown-Bibliothek wäre eine neue Laufzeit-
 * Abhängigkeit im Bundle, für die es hier keinen Auftrag gibt.
 *
 * 🔴 Sicherheitsgrenze: der Eingabetext wird IMMER zuerst HTML-escaped. Damit
 * kann kein `<script>` und kein `onerror=` aus einer Markdown-Datei in die
 * Seite gelangen — auch nicht aus einer fremden, per Freigabe geteilten Datei.
 * Link-Ziele werden zusätzlich auf http/https/mailto eingeschränkt (kein
 * `javascript:`).
 */

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (ch) => ESCAPES[ch]);
}

/** Nur harmlose Schemata — alles andere wird zu einem toten Anker. */
function safeHref(raw: string): string | null {
  const url = raw.trim();
  if (/^(https?:|mailto:)/i.test(url)) {
    return url;
  }
  // relative Ziele sind in Ordnung, solange sie kein Schema tragen
  if (/^[./#?]/.test(url) && !url.includes(":")) {
    return url;
  }
  return null;
}

/**
 * R4 (Bilder-Befund) — nur harmlose Bildquellen: http(s), data:image/* und
 * relative Pfade. Alles andere (javascript:, data:text/html, …) wird verworfen.
 */
function safeImgSrc(raw: string): string | null {
  const url = raw.trim();
  if (/^https?:/i.test(url) || /^data:image\//i.test(url)) {
    return url;
  }
  if (/^[./]/.test(url) && !url.includes(":")) {
    return url;
  }
  return null;
}

/** Inline-Auszeichnung auf bereits escapetem Text. */
function inline(escaped: string): string {
  let out = escaped;
  // `code` zuerst, damit darin keine weitere Auszeichnung greift
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  // R4 — Bilder VOR Links (gleiche Klammer-Syntax mit führendem `!`).
  out = out.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (m, alt, src) => {
    const safe = safeImgSrc(src);
    return safe ? `<img src="${safe}" alt="${alt}" />` : alt;
  });
  // Welle B (Punkt 15) — ein Datei-/URL-Link kann als **Link**, **Button** oder
  // **Vorschau** dargestellt werden. Der Anzeige-Typ reist im optionalen
  // Markdown-Titel-Slot `[Label](ziel "typ")`, damit Markdown die eine Quelle
  // der Wahrheit bleibt (portabel, kein Sonderfeld):
  //   [L](/md/id)                 → Link (Label optional; leer ⇒ Ziel-URL)
  //   [L](/md/id "button")        → Button (Label Pflicht; leer ⇒ Ziel-URL)
  //   [L](/md/id "preview:fill")  → Vorschau, Modus original|fit|fill
  // escapeHtml hat `"` bereits zu `&quot;` gemacht — der Titel matcht darauf.
  out = out.replace(
    /\[([^\]]*)\]\(([^)\s]+)(?:\s+&quot;([^&]*?)&quot;)?\)/g,
    (m, label, href, title) => {
      const safe = safeHref(href);
      if (!safe) {
        return label;
      }
      const t = (title || "").trim();
      const text = label || safe;
      if (t === "button") {
        return `<a href="${safe}" class="ryfe-mdbtn" data-mdtype="button" target="_blank" rel="noreferrer noopener">${text}</a>`;
      }
      const pv = /^preview(?::(original|fit|fill))?$/.exec(t);
      if (pv) {
        const mode = pv[1] || "fit";
        const name = label || "Vorschau";
        // Platzhalter-„Atom": in der Vorschau-Ansicht ersetzt ein Post-Mount-
        // Pass (RyfeMarkdown) diesen Span durch den Live-Inhalts-Embed; im
        // WYSIWYG bleibt er ein nicht editierbarer Chip (contenteditable=false).
        return `<span class="ryfe-mdpreview ryfe-mdpreview--${mode}" data-mdhref="${safe}" data-mdmode="${mode}" data-mdlabel="${name}" contenteditable="false">📄 ${name} ↗</span>`;
      }
      return `<a href="${safe}" target="_blank" rel="noreferrer noopener">${text}</a>`;
    },
  );
  return out;
}

/** Zerlegt eine (bereits escapete) Pipe-Zeile in getrimmte Zellen. */
function tableCells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) {
    s = s.slice(1);
  }
  if (s.endsWith("|")) {
    s = s.slice(0, -1);
  }
  return s.split("|").map((c) => c.trim());
}

/** Trennzeile einer GFM-Tabelle: jede Zelle ist `:?-+:?` (mit ≥1 Bindestrich). */
function isTableDelimiter(line: string): boolean {
  if (!line.includes("-") || !line.includes("|")) {
    return false;
  }
  const cells = tableCells(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

/** Ausrichtung aus einer Trennzellen-Angabe (`:---:` etc.). */
function cellAlign(spec: string): "left" | "right" | "center" | null {
  const l = spec.startsWith(":");
  const r = spec.endsWith(":");
  if (l && r) {
    return "center";
  }
  if (r) {
    return "right";
  }
  if (l) {
    return "left";
  }
  return null;
}

/**
 * Markdown → HTML. Unterstützt Überschriften, Absätze, Listen (geordnet und
 * ungeordnet), Zitate, Code-Blöcke, horizontale Linien, GFM-Pipe-Tabellen und
 * die Inline-Formen.
 */
export function renderMarkdown(src: string): string {
  const lines = escapeHtml(src ?? "").split(/\r?\n/);
  const out: string[] = [];
  // R4 (Punkt 11) — Listen sind verschachtelbar (2 Leerzeichen je Ebene) und
  // `- [ ]` / `- [x]` rendert als echte Checkbox statt als Literaltext.
  const listStack: { type: "ul" | "ol"; indent: number }[] = [];
  let inCode = false;
  const closeList = () => {
    while (listStack.length) {
      out.push(`</${listStack.pop()!.type}>`);
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^```/.test(line.trim())) {
      closeList();
      out.push(inCode ? "</code></pre>" : "<pre><code>");
      inCode = !inCode;
      continue;
    }
    if (inCode) {
      out.push(line);
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      closeList();
      const level = heading[1].length;
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      closeList();
      out.push("<hr />");
      continue;
    }
    const quote = /^&gt;\s?(.*)$/.exec(line);
    if (quote) {
      closeList();
      out.push(`<blockquote>${inline(quote[1])}</blockquote>`);
      continue;
    }
    const li = /^(\s*)([-*+]|\d+\.)\s+(.*)$/.exec(line);
    if (li) {
      const indent = li[1].replace(/\t/g, "  ").length;
      const type: "ul" | "ol" = /^\d/.test(li[2]) ? "ol" : "ul";
      while (
        listStack.length &&
        indent < listStack[listStack.length - 1].indent
      ) {
        out.push(`</${listStack.pop()!.type}>`);
      }
      const top = listStack[listStack.length - 1];
      if (!top || indent > top.indent) {
        out.push(`<${type}>`);
        listStack.push({ type, indent });
      } else if (top.type !== type) {
        out.push(`</${top.type}>`);
        listStack.pop();
        out.push(`<${type}>`);
        listStack.push({ type, indent });
      }
      const task = /^\[( |x|X)\]\s+(.*)$/.exec(li[3]);
      if (task) {
        const checked = task[1].toLowerCase() === "x";
        out.push(
          `<li class="ryfe-task"><input type="checkbox" class="ryfe-task__box"${
            checked ? " checked" : ""
          } /> ${inline(task[2])}</li>`,
        );
      } else {
        out.push(`<li>${inline(li[3])}</li>`);
      }
      continue;
    }
    // GFM-Pipe-Tabelle: Kopfzeile + Trennzeile, dann beliebig viele Datenzeilen.
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      isTableDelimiter(lines[i + 1])
    ) {
      closeList();
      const headers = tableCells(line);
      const aligns = tableCells(lines[i + 1]).map(cellAlign);
      const alignAttr = (idx: number) => {
        const a = aligns[idx];
        return a ? ` style="text-align:${a}"` : "";
      };
      const parts: string[] = ["<table>", "<thead>", "<tr>"];
      headers.forEach((h, idx) => {
        parts.push(`<th${alignAttr(idx)}>${inline(h)}</th>`);
      });
      parts.push("</tr>", "</thead>", "<tbody>");
      let j = i + 2;
      for (; j < lines.length; j++) {
        const bodyLine = lines[j];
        if (!bodyLine.includes("|") || bodyLine.trim() === "") {
          break;
        }
        const cells = tableCells(bodyLine);
        parts.push("<tr>");
        headers.forEach((_h, idx) => {
          parts.push(`<td${alignAttr(idx)}>${inline(cells[idx] ?? "")}</td>`);
        });
        parts.push("</tr>");
      }
      parts.push("</tbody>", "</table>");
      out.push(parts.join("\n"));
      i = j - 1;
      continue;
    }
    if (line.trim() === "") {
      closeList();
      continue;
    }
    closeList();
    out.push(`<p>${inline(line)}</p>`);
  }
  closeList();
  if (inCode) {
    out.push("</code></pre>");
  }
  return out.join("\n");
}
